import { setCookie, deleteCookie } from 'cookies-next';
import { buildSearchParams } from '@utils/data-fetching';

/**
 * "Default my jobs" page — which of the two My Jobs worlds (the employer job lists or the
 * candidate application lists) a user lands in when they open `/my-jobs`.
 *
 * Stored in a plain, non-httpOnly cookie so that the `/my-jobs` server component can read it
 * and redirect with no API call, while `MyJobsSwitcher` (a client component) can write it on
 * mount. Every write re-stamps the window, so the preference slides with use rather than
 * expiring 7 days after first contact.
 */
export const MY_JOBS_COOKIE = 'bj_my_jobs_default';

/** 7 days, in seconds. */
export const MY_JOBS_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;

export type MyJobsDefault = 'employer' | 'candidate';

/** The employer default sorting — the same subpage `/my-jobs` has always redirected to. */
export const EMPLOYER_HOME_PATH = '/my-jobs/creation-date';
export const CANDIDATE_HOME_PATH = '/applied-jobs';

/** Only what `await cookies()` (server) and our tests actually need. */
export interface MyJobsCookieStore {
  get: (name: string) => { value: string } | undefined;
}

const isMyJobsDefault = (value: unknown): value is MyJobsDefault => (
  value === 'employer' || value === 'candidate'
);

/**
 * Server-side read. Pass the result of `await cookies()`. Anything outside the union — a
 * tampered or stale cookie — reads as absent, so the caller falls through to detection.
 */
export const readMyJobsDefault = (cookieStore: MyJobsCookieStore): MyJobsDefault | undefined => {
  const value = cookieStore.get(MY_JOBS_COOKIE)?.value;
  return isMyJobsDefault(value) ? value : undefined;
};

/**
 * The landing URL for a resolved default, locale-unprefixed — callers add the prefix.
 *
 * Employers land on the unfiltered list — no `filter` param, exactly the URL the header's
 * My Jobs link resolved to before the switcher shipped. That is the combined
 * "Active + Inactive" view, so an account whose jobs are all inactive still lands on a list
 * with something in it, and no counters have to be fetched to decide. An incoming `filter`
 * still passes through, so a deep link like `/my-jobs?filter=draft` survives the redirect.
 */
export const myJobsHomeHref = (
  value: MyJobsDefault,
  extraParams: Record<string, unknown> = {},
): string => {
  const path = value === 'candidate' ? CANDIDATE_HOME_PATH : EMPLOYER_HOME_PATH;

  return `${path}${buildSearchParams(extraParams, '?', true)}`;
};

/** Client-side write. No-op on the server (`cookies-next` skips the server render phase). */
export const writeMyJobsDefault = (value: MyJobsDefault): void => {
  if (typeof document === 'undefined') return;

  void setCookie(MY_JOBS_COOKIE, value, {
    path: '/',
    maxAge: MY_JOBS_COOKIE_MAX_AGE,
    sameSite: 'lax',
    // `secure` only where the page is actually served over TLS — local HTTP dev would
    // otherwise drop the cookie silently.
    secure: window.location.protocol === 'https:',
  });
};

/**
 * Client-side clear. Called on sign-out: the cookie is `path=/` and lives for 7 days, so on a
 * shared browser the next person to log in would otherwise be routed into the previous user's
 * My Jobs world.
 */
export const clearMyJobsDefault = (): void => {
  if (typeof document === 'undefined') return;

  void deleteCookie(MY_JOBS_COOKIE, { path: '/' });
};
